import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Users, Activity, Layers, ArrowRightLeft, Eye, AlertCircle, LogOut } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLOR = { admin: "#D4AF37", executive: "#22C55E", consultant: "#3B82F6", viewer: "#94A3B8" };
const SUITE_COLOR = {
  enterprise: "#22C55E", advisory: "#A855F7", investor: "#D4AF37",
  government: "#3B82F6", internal: "#94A3B8",
};

export default function Settings() {
  const { user, refreshBilling } = useAuth();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tab, setTab] = useState("users");
  const [isInternalAdmin, setIsInternalAdmin] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [switchAudit, setSwitchAudit] = useState([]);
  const [activeSwitch, setActiveSwitch] = useState(localStorage.getItem("nexus_tenant_switch") || "");

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (user?.role === "admin" || user?.role === "executive") {
      api.get("/audit-logs").then((r) => setAudit(r.data)).catch(() => {});
    }
    // Only Pandora internal admins should see the Tenant Switch tab
    api.get("/governance/me")
      .then(() => setIsInternalAdmin(true))
      .catch(() => setIsInternalAdmin(false));
  }, [user]);

  const loadTenantSwitch = async () => {
    try {
      const [t, a] = await Promise.all([
        api.get("/governance/tenants"),
        api.get("/governance/tenant-switch-audit"),
      ]);
      setTenants(t.data);
      setSwitchAudit(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tenants");
    }
  };

  useEffect(() => {
    if (tab === "tenants" && isInternalAdmin) loadTenantSwitch();
  }, [tab, isInternalAdmin]);

  const enterTenant = async (orgId) => {
    localStorage.setItem("nexus_tenant_switch", orgId);
    setActiveSwitch(orgId);
    toast.success("Tenant view enabled — read-only");
    await refreshBilling();
    // Hard reload so every page refetches its data with the new scope
    setTimeout(() => { window.location.href = "/"; }, 400);
  };

  const exitTenant = async () => {
    localStorage.removeItem("nexus_tenant_switch");
    setActiveSwitch("");
    toast.success("Returned to Pandora Internal");
    await refreshBilling();
    setTimeout(() => { window.location.href = "/settings"; }, 400);
  };

  return (
    <div className="p-8 space-y-6" data-testid="settings-page">
      <div>
        <div className="label-eyebrow mb-2">// Governance & Security</div>
        <h1 className="font-display text-4xl font-medium text-white tracking-tight">Governance</h1>
        <p className="text-slate-400 mt-2 text-sm">Users, roles, audit log, tenant operations and platform configuration.</p>
      </div>

      {/* Active impersonation banner */}
      {activeSwitch && (
        <div className="nexus-glass-panel p-4 flex items-center justify-between gap-4 border border-amber-500/40" data-testid="impersonation-banner">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(245,158,11,0.18)" }}>
              <Eye className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <div className="text-white text-sm font-medium">Tenant Switch Active — READ ONLY</div>
              <div className="text-xs text-slate-400">Viewing tenant <span className="font-mono text-amber-300">{activeSwitch}</span>. Every request is audit-logged.</div>
            </div>
          </div>
          <button onClick={exitTenant} data-testid="exit-tenant-btn" className="nexus-pill bg-white/[0.05] hover:bg-white/[0.1] text-amber-300 border border-amber-500/40">
            <LogOut className="w-3 h-3" /> Exit
          </button>
        </div>
      )}

      <div className="flex flex-wrap border border-white/10 w-fit rounded-md overflow-hidden">
        {[
          { id: "users", label: "Users", icon: Users },
          { id: "audit", label: "Audit Log", icon: Activity },
          { id: "security", label: "Security", icon: ShieldCheck },
          ...(isInternalAdmin ? [{ id: "tenants", label: "Tenant Switch", icon: Layers }] : []),
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-5 py-2.5 flex items-center gap-2 text-sm font-mono tracking-widest ${tab === t.id ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div className="nexus-card" data-testid="users-table">
          <table className="w-full">
            <thead><tr className="border-b border-white/10">
              {["Name", "Email", "Role", "Department"].map((h) => <th key={h} className="px-5 py-3 text-left label-eyebrow">{h}</th>)}
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-sm text-white">{u.name}</td>
                  <td className="px-5 py-3 text-sm text-slate-400 font-mono">{u.email}</td>
                  <td className="px-5 py-3 font-mono text-xs uppercase tracking-widest" style={{ color: ROLE_COLOR[u.role] }}>{u.role}</td>
                  <td className="px-5 py-3 text-sm text-slate-400">{u.department || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "audit" && (
        <div className="nexus-card" data-testid="audit-table">
          <table className="w-full">
            <thead><tr className="border-b border-white/10">
              {["Timestamp", "Action", "Entity", "Detail"].map((h) => <th key={h} className="px-5 py-3 text-left label-eyebrow">{h}</th>)}
            </tr></thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-xs font-mono text-slate-500">{new Date(a.timestamp).toLocaleString("en-US", { hour12: false })}</td>
                  <td className="px-5 py-3 text-sm font-mono" style={{ color: "var(--gold-400)" }}>{a.action}</td>
                  <td className="px-5 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">{a.entity}</td>
                  <td className="px-5 py-3 text-xs text-slate-400">{a.meta?.title || a.meta?.name || a.meta?.email || a.meta?.path || a.entity_id?.slice(0, 8)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!audit.length && <div className="p-8 text-center text-slate-500 text-sm">No audit entries.</div>}
        </div>
      )}

      {tab === "security" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Role-Based Access Control", "Admin, Executive, Consultant, Viewer with scoped permissions.", "ENABLED"],
            ["Multi-tenant Data Scoping", "org_id enforcement on every list/read/write endpoint.", "ENABLED"],
            ["JWT Session Security", "Signed tokens, 7-day expiry.", "ENABLED"],
            ["Password Hashing", "Bcrypt with auto-generated salts.", "ENABLED"],
            ["Audit Logging", "Every state-changing action recorded with actor + timestamp.", "ENABLED"],
            ["E-Sign Content Hash", "SHA-256 binds signatures to exact proposal content.", "ENABLED"],
          ].map(([title, desc, status]) => (
            <div key={title} className="nexus-card p-5">
              <div className="flex items-start justify-between">
                <ShieldCheck className="w-5 h-5" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
                <span className="font-mono text-[10px] tracking-widest px-2 py-1 border border-emerald-500/30 text-emerald-400">{status}</span>
              </div>
              <div className="mt-3 font-display text-base text-white">{title}</div>
              <div className="text-xs text-slate-400 mt-1.5">{desc}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "tenants" && isInternalAdmin && (
        <div className="space-y-6" data-testid="tenant-switch-panel">
          <div className="nexus-glass-panel p-5 flex items-start gap-3 border border-amber-500/30">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-1 shrink-0" />
            <div className="text-[12px] text-slate-300 leading-relaxed">
              <span className="text-amber-300 font-medium">Restricted developer tooling.</span> Tenant Switch lets you view any
              customer organization's data in <span className="text-white">read-only mode</span> for live support. Writes are
              blocked. Every page view is audit-logged below with your identity, the target tenant, and the requested path.
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-3">// All tenants ({tenants.length})</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tenants.map((t) => {
                const isInternal = t.suite === "internal";
                const isActive = activeSwitch === t.id;
                return (
                  <div key={t.id} className={`nexus-card p-5 ${isActive ? "border-amber-500/50 bg-amber-500/5" : ""}`} data-testid={`tenant-card-${t.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-medium truncate">{t.name}</div>
                        <div className="font-mono text-[10px] text-slate-500 truncate mt-0.5">{t.id}</div>
                      </div>
                      <span className="font-mono text-[9px] tracking-widest px-2 py-0.5 ml-2 shrink-0" style={{ color: SUITE_COLOR[t.suite] || "#94A3B8", border: `1px solid ${SUITE_COLOR[t.suite] || "#94A3B8"}40` }}>
                        {(t.suite || "—").toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                      <div><span className="text-slate-500">Plan: </span><span className="text-white">{t.plan_id || "—"}</span></div>
                      <div><span className="text-slate-500">Status: </span><span className="text-white">{t.status}</span></div>
                      <div><span className="text-slate-500">Users: </span><span className="text-white">{t.user_count}</span></div>
                      <div><span className="text-slate-500">Cycle: </span><span className="text-white">{t.billing_cycle || "—"}</span></div>
                    </div>
                    {t.owner && (
                      <div className="mt-3 pt-3 border-t border-white/5 text-[11px]">
                        <div className="text-slate-500">Org admin</div>
                        <div className="text-white truncate">{t.owner.name}</div>
                        <div className="text-slate-500 font-mono text-[10px] truncate">{t.owner.email}</div>
                      </div>
                    )}
                    <button
                      onClick={() => enterTenant(t.id)}
                      disabled={isInternal || isActive}
                      data-testid={`switch-to-${t.id}`}
                      className={`mt-4 w-full py-2 rounded-md font-mono text-[10px] tracking-widest flex items-center justify-center gap-2 transition-colors ${isInternal ? "border border-white/10 text-slate-600 cursor-not-allowed" : isActive ? "bg-amber-500/20 border border-amber-500/40 text-amber-300" : "border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"}`}
                    >
                      <ArrowRightLeft className="w-3 h-3" />
                      {isInternal ? "INTERNAL ORG" : isActive ? "CURRENTLY VIEWING" : "VIEW READ-ONLY"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-3">// Tenant-switch audit ({switchAudit.length} most recent)</div>
            <div className="nexus-card overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-white/10">
                  {["When", "Operator", "Target tenant", "Path"].map((h) => <th key={h} className="px-5 py-3 text-left label-eyebrow">{h}</th>)}
                </tr></thead>
                <tbody>
                  {switchAudit.map((r) => (
                    <tr key={r.id} className="border-b border-white/5">
                      <td className="px-5 py-3 text-xs font-mono text-slate-500">{new Date(r.timestamp).toLocaleString("en-US", { hour12: false })}</td>
                      <td className="px-5 py-3 text-xs text-white">{r.actor?.name || r.actor_id?.slice(0, 8)}</td>
                      <td className="px-5 py-3 text-xs font-mono text-amber-300">{r.entity_id}</td>
                      <td className="px-5 py-3 text-xs font-mono text-slate-400 truncate max-w-[280px]">{r.meta?.path || "—"}</td>
                    </tr>
                  ))}
                  {switchAudit.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-sm text-slate-500">No tenant-switch events yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
